/**
 * NotificationSettingsPage -- Benachrichtigungen verwalten
 * =========================================================
 * Pro Notification-Typ kann der User waehlen:
 *   - Benachrichtigung im System (Bell)
 *   - Benachrichtigung per E-Mail
 */
import { useState, useEffect, useCallback } from 'react'
import { Mail, Bell, GraduationCap, UserPlus, AlertTriangle, FileText, Check, Zap, Clock } from 'lucide-react'
import type { NotificationEmailMode, NotificationPrefs, NotificationType, NotificationTypeInfo } from '@/types'

const API = '/api/notifications'

async function request<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...opts.headers },
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || data.error || 'Anfrage fehlgeschlagen')
  return data
}

const TYPE_ICONS: Record<NotificationType, React.ReactNode> = {
  new_grade: <GraduationCap size={22} />,
  dualmatch_lead: <UserPlus size={22} />,
  exam_failed_critical: <AlertTriangle size={22} />,
  new_invoice: <FileText size={22} />,
}

const TYPE_COLORS: Record<NotificationType, { bg: string; fg: string; ring: string }> = {
  new_grade:      { bg: 'bg-blue-50',   fg: 'text-blue-600',   ring: 'ring-blue-100' },
  dualmatch_lead: { bg: 'bg-fom-50',    fg: 'text-fom-600',    ring: 'ring-fom-100' },
  exam_failed_critical:  { bg: 'bg-amber-50',  fg: 'text-amber-600',  ring: 'ring-amber-100' },
  new_invoice:    { bg: 'bg-purple-50', fg: 'text-purple-600', ring: 'ring-purple-100' },
}

export default function NotificationSettingsPage() {
  const [types, setTypes] = useState<NotificationTypeInfo[]>([])
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<NotificationType | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [savedFlash, setSavedFlash] = useState(false)
  const [testing, setTesting] = useState<NotificationType | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [typesRes, prefsRes] = await Promise.all([
        request<{ data: NotificationTypeInfo[] }>('/types'),
        request<{ data: NotificationPrefs }>('/prefs'),
      ])
      setTypes(typesRes.data || [])
      setPrefs(prefsRes.data || null)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function updatePref(
    type: NotificationType,
    patch: Partial<NotificationPrefs[NotificationType]>,
  ) {
    if (!prefs) return
    const next = { ...prefs, [type]: { ...prefs[type], ...patch } }
    setPrefs(next)
    setSaving(type)
    try {
      await request<{ data: NotificationPrefs }>('/prefs', {
        method: 'PUT',
        body: JSON.stringify({ [type]: next[type] }),
      })
      setSavedFlash(true)
      setTimeout(() => setSavedFlash(false), 1500)
    } catch (e: any) {
      setError(e.message)
      setPrefs(prefs)
    } finally {
      setSaving(null)
    }
  }

  async function handleTest(type: NotificationType) {
    setTesting(type)
    try {
      await request('/test', { method: 'POST', body: JSON.stringify({ type }) })
      setSavedFlash(true)
      setTimeout(() => setSavedFlash(false), 2000)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setTesting(null)
    }
  }

  if (loading) {
    return <div className="p-12 text-center text-gray-400">Laden...</div>
  }

  return (
    <div className="max-w-4xl mx-auto px-8 py-10">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="flex-1">
          <h1 className="text-3xl font-display font-black text-gray-900">Benachrichtigungen</h1>
          <p className="text-sm text-gray-500 mt-1">
            Lege fest, wann und wie du benachrichtigt werden moechtest.
          </p>
        </div>
        {savedFlash && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-sm font-semibold">
            <Check size={14} />
            Gespeichert
          </div>
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
          {error}
        </div>
      )}

      {/* Channel-Legende */}
      <div className="mb-6 grid grid-cols-2 gap-4">
        <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200">
          <div className="w-10 h-10 rounded-lg bg-fom-50 text-fom-600 flex items-center justify-center">
            <Bell size={18} />
          </div>
          <div>
            <div className="text-sm font-bold text-gray-900">Im System</div>
            <div className="text-xs text-gray-500">Erscheint in der Glocke oben rechts</div>
          </div>
        </div>
        <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200">
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
            <Mail size={18} />
          </div>
          <div>
            <div className="text-sm font-bold text-gray-900">Per E-Mail</div>
            <div className="text-xs text-gray-500">Wird an deine Adresse gesendet</div>
          </div>
        </div>
      </div>

      {/* Notification-Typen */}
      <div className="space-y-3">
        {types.map(t => {
          const pref = prefs?.[t.id] || {
            email_enabled: false,
            system_enabled: true,
            email_mode: 'immediate' as NotificationEmailMode,
            email_daily_time: '08:00',
          }
          const colors = TYPE_COLORS[t.id]
          const isSaving = saving === t.id

          return (
            <div
              key={t.id}
              className="bg-white rounded-xl border border-gray-200 p-5 transition-shadow hover:shadow-sm"
            >
              <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-12 h-12 rounded-xl ${colors.bg} ${colors.fg} flex items-center justify-center ring-4 ${colors.ring}`}>
                  {TYPE_ICONS[t.id]}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <h3 className="text-base font-bold text-gray-900">{t.label}</h3>
                    <button
                      type="button"
                      onClick={() => handleTest(t.id)}
                      disabled={testing === t.id}
                      className="text-[11px] font-semibold text-gray-400 hover:text-fom-600 transition-colors px-2 py-1 rounded disabled:opacity-50"
                      title="Test-Benachrichtigung erstellen"
                    >
                      {testing === t.id ? 'Sende...' : 'Testen'}
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed mb-4">{t.description}</p>

                  <div className="flex items-center gap-3 flex-wrap">
                    <ChannelToggle
                      icon={<Bell size={14} />}
                      label="Im System"
                      checked={pref.system_enabled}
                      onChange={v => updatePref(t.id, { system_enabled: v })}
                      disabled={isSaving}
                    />
                    <ChannelToggle
                      icon={<Mail size={14} />}
                      label="E-Mail"
                      checked={pref.email_enabled}
                      onChange={v => updatePref(t.id, { email_enabled: v })}
                      disabled={isSaving}
                    />
                  </div>

                  {/* Mail-Versand-Modus (nur wenn E-Mail aktiv) */}
                  {pref.email_enabled && (
                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-4 flex-wrap">
                      <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        <Mail size={12} />
                        Versand
                      </div>
                      <ModeToggle
                        mode={pref.email_mode}
                        onChange={(mode) => updatePref(t.id, { email_mode: mode })}
                        disabled={isSaving}
                      />
                      {pref.email_mode === 'daily' && (
                        <div className="flex items-center gap-2">
                          <Clock size={14} className="text-gray-400" />
                          <input
                            type="time"
                            value={pref.email_daily_time}
                            onChange={(e) => updatePref(t.id, { email_daily_time: e.target.value })}
                            disabled={isSaving}
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 focus:border-fom-500 focus:outline-none disabled:opacity-50"
                          />
                          <span className="text-xs text-gray-400">Uhr</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="mt-8 p-5 bg-gray-50 rounded-xl border border-gray-200 text-xs text-gray-500 leading-relaxed">
        <strong className="text-gray-700">Hinweis:</strong> E-Mails werden an die im
        Benutzerprofil hinterlegte Adresse gesendet. Aenderungen werden automatisch
        gespeichert. Mit "Testen" kannst du eine Beispiel-Benachrichtigung ausloesen.
      </div>
    </div>
  )
}

function ModeToggle({
  mode, onChange, disabled,
}: {
  mode: NotificationEmailMode
  onChange: (v: NotificationEmailMode) => void
  disabled?: boolean
}) {
  const options: { value: NotificationEmailMode; label: string; icon: React.ReactNode }[] = [
    { value: 'immediate', label: 'Sofort', icon: <Zap size={12} /> },
    { value: 'daily',     label: '1× täglich', icon: <Clock size={12} /> },
  ]
  return (
    <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          disabled={disabled}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
            mode === opt.value
              ? 'bg-white text-fom-700 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          {opt.icon}
          {opt.label}
        </button>
      ))}
    </div>
  )
}


function ChannelToggle({
  icon, label, checked, onChange, disabled,
}: {
  icon: React.ReactNode
  label: string
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      disabled={disabled}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 text-sm font-semibold transition-all ${
        checked
          ? 'bg-fom-500 border-fom-500 text-white shadow-sm'
          : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span className={checked ? 'text-white' : 'text-gray-400'}>{icon}</span>
      {label}
      <span
        className={`w-9 h-5 rounded-full relative transition-colors ${
          checked ? 'bg-white/30' : 'bg-gray-200'
        }`}
      >
        <span
          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${
            checked ? 'left-[18px]' : 'left-0.5'
          }`}
        />
      </span>
    </button>
  )
}
